import { downloadFromUrl } from '@/lib/download';
import { Download, X } from 'lucide-react';
import { useContext, useEffect, useState } from 'react';
import { useRecoilValue, useSetRecoilState } from 'recoil';

import {
    ChainlitContext,
    type IMessageElement,
    elementState,
    sessionIdState,
    sideViewState
} from '@chainlit/react-client';

import { ConfirmDeleteDialog } from '@/components/ConfirmDeleteDialog';
import {
    FILE_CARD_HOVER_CLASS,
    FileCardContent
} from '@/components/Elements/FileCardContent';
import { Button } from '@/components/ui/button';
import {
    Dialog,
    DialogClose,
    DialogContent,
    DialogHeader,
    DialogTitle
} from '@/components/ui/dialog';

import type { PanelContentProps } from '../types';
import { CsvTablePreview } from './CsvTablePreview';

interface ModalFile {
    name: string;
    url: string;
    type: string;
    originalUrl?: string;
}

function isOfficeFile(fileName?: string, mimeType?: string): boolean {
    if (fileName) {
        const ext = fileName.toLowerCase().split('.').pop() || '';
        if (
            [
                'xlsx',
                'xls',
                'xlsm',
                'xlsb',
                'docx',
                'doc',
                'pptx',
                'ppt'
            ].includes(ext)
        ) {
            return true;
        }
    }
    if (mimeType) {
        const officeMimeTypes = [
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            'application/vnd.ms-excel',
            'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
            'application/msword',
            'application/vnd.openxmlformats-officedocument.presentationml.presentation',
            'application/vnd.ms-powerpoint'
        ];
        if (officeMimeTypes.includes(mimeType)) {
            return true;
        }
    }
    return false;
}

function previewFor(
    element: IMessageElement,
    sessionId: string | undefined
): ModalFile | null {
    if (!element.url) return null;

    if (isOfficeFile(element.name, element.mime)) {
        return {
            name: element.name || 'file',
            url: `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(
                element.url
            )}`,
            type: element.type || 'file',
            originalUrl: element.url
        };
    }

    const urlWithSession = element.url.includes('session_id')
        ? element.url
        : `${element.url}${
              element.url.includes('?') ? '&' : '?'
          }session_id=${sessionId}`;

    return {
        name: element.name || 'file',
        url: urlWithSession,
        type: element.type || 'file'
    };
}

interface UploadsPanelProps extends PanelContentProps {
    previewRequestId: string | null;
    onPreviewRequestHandled: () => void;
}

export function UploadsPanel({
    elements,
    previewRequestId,
    onPreviewRequestHandled
}: UploadsPanelProps) {
    const [modalFile, setModalFile] = useState<ModalFile | null>(null);
    const [fileToDelete, setFileToDelete] = useState<{
        id: string;
        name: string;
    } | null>(null);
    const sessionId = useRecoilValue(sessionIdState);
    const apiClient = useContext(ChainlitContext);
    const setSideView = useSetRecoilState(sideViewState);
    const setElements = useSetRecoilState(elementState);

    useEffect(() => {
        if (!previewRequestId) return;
        const requested = elements.find((el) => el.id === previewRequestId);
        if (!requested) return;
        if (!requested.url && requested.chainlitKey && apiClient && sessionId) {
            requested.url = apiClient.getElementUrl(
                requested.chainlitKey,
                sessionId
            );
        }
        const preview = previewFor(requested, sessionId);
        if (preview) setModalFile(preview);
        onPreviewRequestHandled();
    }, [previewRequestId, elements, apiClient, sessionId, onPreviewRequestHandled]);

    if (elements.length === 0) {
        return (
            <div className="flex flex-col justify-center items-center gap-y-1 py-8 text-center">
                <p className="text-secondary-foreground text-base">
                    No files uploaded
                </p>
                <p className="text-muted-foreground text-xs">
                    Uploaded files will appear here
                </p>
            </div>
        );
    }

    const handleFileClick = (element: (typeof elements)[0]) => {
        const preview = previewFor(element, sessionId);
        if (preview) setModalFile(preview);
    };

    const handleDelete = async (elementId: string) => {
        if (!sessionId) return;

        try {
            const response = await fetch(
                `/project/file/${elementId}?session_id=${sessionId}`,
                {
                    method: 'DELETE',
                    headers: { 'Content-Type': 'application/json' }
                }
            );

            if (response.ok) {
                setSideView((prev) => {
                    if (!prev) return prev;
                    return {
                        ...prev,
                        elements: prev.elements.filter(
                            (el) => el.id !== elementId
                        )
                    };
                });
                setElements((prev) => prev.filter((el) => el.id !== elementId));
            }
        } catch {
            // Silent error handling
        }
    };

    const handleDownload = () => {
        if (!modalFile) return;
        downloadFromUrl(modalFile.originalUrl || modalFile.url, modalFile.name);
    };

    const handleDownloadElement = (
        e: React.MouseEvent,
        element: (typeof elements)[0]
    ) => {
        e.stopPropagation();
        if (!element.url) return;
        downloadFromUrl(element.url, element.name || 'file');
    };

    return (
        <>
            <div className="flex flex-col gap-3">
                {elements.map((element) => {
                    if (
                        !element.url &&
                        element.chainlitKey &&
                        apiClient &&
                        sessionId
                    ) {
                        element.url = apiClient.getElementUrl(
                            element.chainlitKey,
                            sessionId
                        );
                    }

                    return (
                        <div
                            key={element.id}
                            className={`flex items-center justify-between gap-4 bg-background ${FILE_CARD_HOVER_CLASS} p-4 border border-border rounded-lg w-full transition-colors cursor-pointer`}
                            onClick={() => handleFileClick(element)}
                        >
                            <FileCardContent name={element.name} />

                            <div className="flex shrink-0 items-center gap-2">
                                <Button
                                    variant="tertiary"
                                    size="default"
                                    onClick={(e) =>
                                        handleDownloadElement(e, element)
                                    }
                                    className="text-sm"
                                >
                                    <Download />
                                    Download
                                </Button>

                                <Button
                                    variant="ghost"
                                    size="icon-compact"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setFileToDelete({
                                            id: element.id,
                                            name: element.name || 'this file'
                                        });
                                    }}
                                    title="Delete file"
                                >
                                    <X />
                                </Button>
                            </div>
                        </div>
                    );
                })}
            </div>

            <ConfirmDeleteDialog
                open={!!fileToDelete}
                onOpenChange={(open) => !open && setFileToDelete(null)}
                title="Delete file"
                description={
                    <>
                        This will permanently delete{' '}
                        <span className="font-medium text-foreground">
                            {fileToDelete?.name}
                        </span>{' '}
                        from this chat. This action cannot be undone.
                    </>
                }
                onConfirm={async () => {
                    if (fileToDelete) await handleDelete(fileToDelete.id);
                    setFileToDelete(null);
                }}
            />

            <Dialog
                open={!!modalFile}
                onOpenChange={(open) => !open && setModalFile(null)}
            >
                <DialogContent
                    hideClose
                    className="flex flex-col w-full max-w-[90vw] h-full max-h-[90vh]"
                >
                    <DialogHeader className="flex-row items-center gap-4 pb-2 shrink-0">
                        <DialogTitle className="flex-1 min-w-0 truncate">
                            <span>{modalFile?.name}</span>
                        </DialogTitle>
                        <div className="flex items-center gap-2 shrink-0">
                            {modalFile &&
                                !modalFile.name
                                    .toLowerCase()
                                    .endsWith('.pdf') && (
                                    <Button
                                        variant="tertiary"
                                        size="default"
                                        className="text-sm"
                                        onClick={handleDownload}
                                    >
                                        <Download />
                                        Download
                                    </Button>
                                )}
                            <DialogClose asChild>
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    className="[&_svg:not([data-spinner])]:size-4"
                                >
                                    <X />
                                    <span className="sr-only">Close</span>
                                </Button>
                            </DialogClose>
                        </div>
                    </DialogHeader>
                    <div className="flex-1 min-h-0 overflow-hidden">
                        {modalFile && /\.csv$/i.test(modalFile.name) && (
                            <CsvTablePreview url={modalFile.url} />
                        )}
                        {modalFile && !/\.csv$/i.test(modalFile.name) && (
                            <iframe
                                src={modalFile.url}
                                className="border-0 rounded-md w-full h-full"
                                title={modalFile.name}
                            />
                        )}
                    </div>
                </DialogContent>
            </Dialog>
        </>
    );
}
